import { forwardRef } from "react";
import CodeMirror, { EditorView, type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { cn } from "@/lib/utils";

export type CodeEditorRef = ReactCodeMirrorRef;

interface CodeEditorProps {
    value: string;
    onChange: (value: string) => void;
    /** CSS height of the editor viewport, e.g. "26rem". */
    height?: string;
    className?: string;
    placeholder?: string;
}

/**
 * A CodeMirror-backed HTML source editor: syntax highlighting, line numbers,
 * bracket/tag matching, auto-indent and search come from CodeMirror's basic
 * setup. Expose the CodeMirror ref so callers can manipulate the document
 * (e.g. insert a snippet at the cursor) via `ref.current.view`.
 */
export const CodeEditor = forwardRef<CodeEditorRef, CodeEditorProps>(function CodeEditor(
    { value, onChange, height = "26rem", className, placeholder },
    ref,
) {
    return (
        <div className={cn("overflow-hidden rounded-md border border-input text-sm", className)}>
            <CodeMirror
                ref={ref}
                value={value}
                onChange={onChange}
                height={height}
                placeholder={placeholder}
                extensions={[html(), EditorView.lineWrapping]}
                basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                    highlightActiveLine: true,
                    autocompletion: true,
                    bracketMatching: true,
                    indentOnInput: true,
                }}
            />
        </div>
    );
});
